import {
	AfterContentChecked,
	Component,
	EventEmitter,
	inject,
	Input,
	Output,
} from '@angular/core';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import {
	ItTableComponent,
	ItIconComponent,
	ItModalComponent,
	ItAccordionComponent,
	ItCheckboxComponent,
	ItInputComponent,
	ItSelectComponent,
	ItButtonDirective,
	ItProgressButtonComponent,
} from 'design-angular-kit';
import { TranslateModule } from '@ngx-translate/core';
import { IterlexService } from '../iterlex.service';
import { DocumentComponent } from '../document/document.component';
import { FilesComponent } from '../files/files.component';
import type { Authority } from '../interfaces/authority';
import type { Dossier } from '../interfaces/dossier';
import type { Phase } from '../interfaces/phase';
import type { Step } from '../interfaces/step';
import type { Document } from '../interfaces/document';
import type { DocumentType } from '../interfaces/document-type';
import type { DocumentSubtype } from '../interfaces/document-subtype';
import type { Version } from '../interfaces/version';
import type { DocumentComplete } from '../interfaces/document-complete';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { compare } from 'natural-orderby';

@Component({
	selector: 'app-amendments',
	imports: [
		FormsModule,
		ItTableComponent,
		ItIconComponent,
		ItModalComponent,
		ItAccordionComponent,
		ItCheckboxComponent,
		ItInputComponent,
		ItSelectComponent,
		ItButtonDirective,
		ItProgressButtonComponent,
		TranslateModule,
		DatePipe,
		UpperCasePipe,
		DocumentComponent,
		FilesComponent,
	],
	templateUrl: './amendments.component.html',
	styleUrl: './amendments.component.scss',
})
export class AmendmentsComponent implements AfterContentChecked {
	private iterlexService = inject(IterlexService);

	@Input({ required: true }) dossier!: Dossier;
	@Input({ required: true }) authority!: Authority;
	@Input({ required: true }) body!: Authority;
	@Input({ required: true }) phase!: Phase;
	@Input({ required: true }) step!: Step;
	@Input() nextStep!: Step;
	@Input({ required: true }) active!: boolean;
	@Output() setCurrentPhaseEvent = new EventEmitter<string>();
	wasLoaded = false;
	progressDeleteVersionUri = '';
	progressDeleteDocumentUri = '';
	amendmentTypesWithOrder: Record<string, number> = {
		repeal: 1,
		substitution: 2,
		insertion: 6,
		join: 3,
		split: 4,
		renumbering: 5,
		modification: 7,
	};
	amendmentTypes = Object.keys(this.amendmentTypesWithOrder);
	languages = environment.documentLanguages;
	documentTypes: DocumentType[] = [];
	documentSubtypes: DocumentSubtype[] = [];
	phases: Phase[] = [];
	steps: Record<string, Step[]> = {};
	currentSteps: string[] = [];
	currentDocument!: Document;
	currentVersion?: Version;
	createSubamenment = false;
	documentsComplete: DocumentComplete[] = [];
	attachments: Record<string, Document[]> = {};
	proponents: string[] = [];
	selectedSteps = new Set<string>();
	deselectedSteps = new Set<string>();
	ngAfterContentChecked(): void {
		if (this.active && !this.wasLoaded) {
			this.wasLoaded = true;
			this.getDocuments();
			this.iterlexService
				.getDocumentTypes(
					new HttpParams().append('authority', this.authority.id),
				)
				.subscribe((documentTypes) => {
					this.documentTypes = documentTypes;
				});
			this.iterlexService
				.getDocumentSubtypes(
					new HttpParams().append('authority', this.authority.id),
				)
				.subscribe((documentSubtypes) => {
					this.documentSubtypes = documentSubtypes;
				});
		}
		if (!this.active && this.wasLoaded) {
			this.wasLoaded = false;
		}
	}
	getDocuments() {
		this.iterlexService
			.getDocuments(
				new HttpParams()
					.append('dossierUri', this.dossier.workUri)
					.append('authority', this.authority.id)
					.append('body', this.body.id)
					.append('phaseName', this.phase.id)
					.append('stepId', this.step.id),
			)
			.subscribe(async (documents) => {
				const newDocumentsComplete = [];
				for (const document of documents.value) {
					const documentComplete = await firstValueFrom(
						this.iterlexService.getDocumentComplete(
							this.authority.id,
							this.body.id,
							document.workUri,
							this.phase.id,
							this.step.id,
							this.dossier.workUri,
						),
					);
					this.attachments[documentComplete.workUri] = [];
					for (const component of documentComplete.components) {
						this.iterlexService
							.getDocumentComplete(
								this.authority.id,
								this.body.id,
								component.childUri,
								this.phase.id,
								this.step.id,
								this.dossier.workUri,
							)
							.subscribe((attachmentComplete) => {
								this.attachments[documentComplete.workUri].push(
									attachmentComplete,
								);
							});
					}
					newDocumentsComplete.push(documentComplete);
				}
				this.documentsComplete = newDocumentsComplete;
				this.sortAmendments();
			});
	}
	getProponents(proponents: Version['metadata']['proponents']) {
		return [...(proponents ?? [])];
	}
	sortAmendments() {
		this.documentsComplete.sort(
			(a, b) =>
				(a.versions.at(-1)?.metadata.order ?? 0) -
				(b.versions.at(-1)?.metadata.order ?? 0),
		);
	}
	getDocumentComplete(workUri: string) {
		this.iterlexService
			.getDocumentComplete(
				this.authority.id,
				this.body.id,
				workUri,
				this.phase.id,
				this.step.id,
				this.dossier.workUri,
			)
			.subscribe((documentComplete) => {
				const index = this.documentsComplete.findIndex(
					(e) => e.workUri == documentComplete.workUri,
				);
				if (index != -1) {
					this.documentsComplete[index] = documentComplete;
					this.attachments[documentComplete.workUri] = [];
					this.attachments[documentComplete.workUri] = [];
					for (const component of documentComplete.components) {
						this.iterlexService
							.getDocumentComplete(
								this.authority.id,
								this.body.id,
								component.childUri,
								this.phase.id,
								this.step.id,
								this.dossier.workUri,
							)
							.subscribe((attachmentComplete) => {
								this.attachments[documentComplete.workUri].push(
									attachmentComplete,
								);
							});
					}
				}
			});
	}
	getPhasesAndSteps() {
		this.steps = {};
		this.selectedSteps.clear();
		this.deselectedSteps.clear();
		this.iterlexService
			.getPhases(this.authority.id, this.body.id, this.dossier.type)
			.subscribe((phases) => {
				this.phases = phases;
				for (const phase of phases) {
					this.iterlexService
						.getSteps(
							this.authority.id,
							this.body.id,
							this.dossier.workUri,
							phase.id,
						)
						.subscribe((steps) => {
							this.steps[phase.id] = steps;
						});
				}
			});
	}
	getVersionSteps(expressionUri: string) {
		this.iterlexService
			.getVersionSteps(expressionUri, this.authority.id, this.body.id)
			.subscribe((steps) => {
				this.currentSteps = steps;
			});
	}
	onCreateAmendmentSubmit(form: NgForm) {
		if (form.valid) {
			this.iterlexService
				.createDocument(
					this.authority.id,
					this.body.id,
					'amendment',
					'eme',
					this.dossier.author,
					form.value.date,
					this.dossier.number +
						'-' +
						form.value.article +
						'-' +
						this.proponents[0].replaceAll(' ', ''),
					form.value.title,
					this.dossier.workUri,
					'main',
				)
				.subscribe((document) => {
					this.iterlexService
						.createVersion(
							document.workUri,
							document.workUri,
							this.dossier.number +
								'-' +
								form.value.article +
								'-' +
								this.proponents[0].replaceAll(' ', ''),
							this.authority.id,
							this.body.id,
							form.value.language,
							document.title,
							this.iterlexService.dateTimeWithTimeZone(
								form.value.date,
								form.value.time,
							),
							document.author,
							{
								amendmentType: form.value.amendmentType,
								article: form.value.article,
								proponents: this.proponents,
							},
						)
						.subscribe((version) => {
							this.iterlexService
								.setVersionStep(
									this.authority.id,
									this.body.id,
									version.expressionUri,
									this.phase.id,
									this.step.id,
								)
								.subscribe(() => {
									this.getDocuments();
								});
						});
				});
		}
	}
	onEditDocumentSubmit(form: NgForm) {
		if (form.valid) {
			this.iterlexService
				.editDocument(
					this.currentDocument.workUri,
					this.authority.id,
					this.body.id,
					form.value.title,
				)
				.subscribe(() => {
					this.currentDocument.title = form.value.title;
				});
		}
	}
	onCreateVersionSubmit(form: NgForm) {
		if (form.valid) {
			this.iterlexService
				.createVersion(
					this.currentDocument.workUri,
					this.iterlexService.getMainAlias(
						this.currentDocument,
						this.body.id,
					).uri,
					this.iterlexService.getMainAlias(
						this.currentDocument,
						this.body.id,
					).number,
					this.authority.id,
					this.body.id,
					form.value.language,
					this.currentDocument.title,
					this.iterlexService.dateTimeWithTimeZone(
						form.value.date,
						form.value.time,
					),
					this.currentDocument.author,
					{
						amendmentType: form.value.amendmentType,
						article: form.value.article,
						proponents: this.proponents,
						parent: this.currentVersion?.expressionUri,
					},
				)
				.subscribe((version) => {
					this.iterlexService
						.setVersionStep(
							this.authority.id,
							this.body.id,
							version.expressionUri,
							this.phase.id,
							this.step.id,
						)
						.subscribe(() => {
							this.getDocumentComplete(
								this.currentDocument.workUri,
							);
						});
				});
		}
	}
	onCreateAttachmentSubmit(form: NgForm) {
		if (form.valid) {
			const [documentType, documentSubtype] =
				form.value.documentSubtype.split('/');
			this.iterlexService
				.createDocument(
					this.authority.id,
					this.body.id,
					documentType,
					documentSubtype,
					this.currentDocument.author,
					form.value.date,
					form.value.number,
					form.value.title,
					this.dossier.workUri,
					'attachment',
				)
				.subscribe((document) => {
					this.iterlexService
						.createVersion(
							document.workUri,
							document.workUri,
							form.value.number,
							this.authority.id,
							this.body.id,
							form.value.language,
							document.title,
							this.iterlexService.dateTimeWithTimeZone(
								form.value.date,
								form.value.time,
							),
							document.author,
						)
						.subscribe((version) => {
							this.iterlexService
								.setVersionStep(
									this.authority.id,
									this.body.id,
									version.expressionUri,
									this.phase.id,
									this.step.id,
								)
								.subscribe(() => {
									this.iterlexService
										.createComponent(
											this.authority.id,
											this.body.id,
											this.dossier.workUri,
											this.currentDocument.workUri,
											document.workUri,
										)
										.subscribe(() => this.getDocuments());
								});
						});
				});
		}
	}
	onEditVersionSubmit(form: NgForm) {
		this.iterlexService
			.editVersion(
				this.currentVersion!.expressionUri,
				this.authority.id,
				this.body.id,
				{
					amendmentType: form.value.amendmentType,
					article: form.value.article,
					proponents: this.proponents,
				},
			)
			.subscribe(() => this.getDocuments());
	}
	deleteDocument(workUri: string) {
		this.progressDeleteDocumentUri = workUri;
		this.iterlexService
			.deleteDocument(workUri, this.authority.id, this.body.id)
			.subscribe({
				next: () => {
					this.getDocuments();
				},
				complete: () => {
					this.progressDeleteDocumentUri = '';
				},
			});
	}
	changeStep(phaseId: string, stepId: string) {
		if (stepId == this.step.id) {
			return;
		}
		const id = phaseId + '/' + stepId;
		if (this.currentSteps.includes(stepId)) {
			if (this.deselectedSteps.has(id)) {
				this.deselectedSteps.delete(id);
			} else {
				this.deselectedSteps.add(id);
			}
		} else {
			if (this.selectedSteps.has(id)) {
				this.selectedSteps.delete(id);
			} else {
				this.selectedSteps.add(id);
			}
		}
	}
	async onMoveVersionSubmit(form: NgForm) {
		if (form.valid) {
			const selectedPhases = new Set<string>();
			for (const selectedStep of this.selectedSteps) {
				const [phaseId, stepId] = selectedStep.split('/');
				await firstValueFrom(
					this.iterlexService.setVersionStep(
						this.authority.id,
						this.body.id,
						this.currentVersion!.expressionUri,
						phaseId,
						stepId,
					),
				);
				selectedPhases.add(phaseId);
			}
			for (const deselectedStep of this.deselectedSteps) {
				const [, stepId] = deselectedStep.split('/');
				await firstValueFrom(
					this.iterlexService.deleteVersionStep(
						this.currentVersion!.expressionUri,
						this.authority.id,
						this.body.id,
						stepId,
					),
				);
			}
			const lastPhase = this.phases
				.filter((phase) => selectedPhases.has(phase.id))
				.pop();
			if (lastPhase) {
				this.setCurrentPhaseEvent.emit(lastPhase.id);
			}
		}
	}
	deleteVersion(expressionUri: string) {
		this.progressDeleteVersionUri = expressionUri;
		this.iterlexService
			.deleteVersion(expressionUri, this.authority.id, this.body.id)
			.subscribe({
				next: () => {
					this.getDocumentComplete(this.currentDocument.workUri);
				},
				complete: () => {
					this.progressDeleteVersionUri = '';
				},
			});
	}
	moveLastVersionsToStep(stepId: string) {
		this.iterlexService
			.getDocuments(
				new HttpParams()
					.append('dossierUri', this.dossier.workUri)
					.append('authority', this.authority.id)
					.append('body', this.body.id)
					.append('phaseName', this.phase.id)
					.append('stepId', stepId),
			)
			.subscribe(async (documents) => {
				for (const document of documents.value) {
					const documentComplete = await firstValueFrom(
						this.iterlexService.getDocumentComplete(
							this.authority.id,
							this.body.id,
							document.workUri,
							this.phase.id,
							stepId,
						),
					);
					for (const version of documentComplete.versions) {
						await firstValueFrom(
							this.iterlexService.deleteVersionStep(
								version.expressionUri,
								this.authority.id,
								this.body.id,
								stepId,
							),
						);
					}
				}
				let number = 1;
				for (const document of this.documentsComplete) {
					if (document.versions.length > 0) {
						const versions = [
							document.versions
								.filter((version) => !version.metadata.parent)
								.at(-1)!,
						];
						versions.push(
							...document.versions.filter(
								(version) =>
									version.metadata.parent ==
									versions[0].expressionUri,
							),
						);
						for (const version of versions) {
							this.iterlexService
								.setVersionStep(
									this.authority.id,
									this.body.id,
									version.expressionUri,
									this.phase.id,
									stepId,
								)
								.subscribe();
						}
						if (stepId == 'ordinamentoEmendamenti') {
							this.iterlexService
								.editVersion(
									versions[0].expressionUri,
									this.authority.id,
									this.body.id,
									{
										...versions[0].metadata,
										order: number++,
									},
								)
								.subscribe();
						}
					}
				}
			});
	}
	save() {
		for (const document of this.documentsComplete) {
			for (const version of document.versions) {
				this.iterlexService
					.editVersion(
						version.expressionUri,
						this.authority.id,
						this.body.id,
						version.metadata,
					)
					.subscribe();
			}
		}
	}
	orderAmendment(index: number, change: number) {
		this.documentsComplete[index].versions[0].metadata.order! += change;
		this.documentsComplete[index + change].versions[0].metadata.order! -=
			change;
		this.sortAmendments();
	}
	sortAmendmentsAutomatically() {
		this.documentsComplete.sort(
			(a, b) =>
				compare()(
					a.versions.at(-1)?.metadata.article ?? '',
					b.versions.at(-1)?.metadata.article ?? '',
				) ||
				this.amendmentTypesWithOrder[
					a.versions.at(-1)?.metadata.amendmentType ?? ''
				] -
					this.amendmentTypesWithOrder[
						b.versions.at(-1)?.metadata.amendmentType ?? ''
					] ||
				Date.parse(a.versions.at(-1)?.date ?? '') -
					Date.parse(b.versions.at(-1)?.date ?? ''),
		);
		let number = 1;
		for (const document of this.documentsComplete) {
			const lastVersion = document.versions.at(-1);
			if (lastVersion) {
				lastVersion.metadata.order = number++;
			}
		}
	}
}
